import type { ChangeEvent, FC, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import { LabelContainer } from '@motorway/motorway-component-library';
import { Radio } from '@motorway/mw-highway-code/alpha';

import ExpandableFilter from 'Components/shared/ExpandableFilter/ExpandableFilter';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';

import useFilterView from '../useFilterView';

import styles from './RadioGroupFilterView.module.scss';

export interface RadioGroupOption {
	label: string;
	value: string;
}

export interface RadioGroupFilterViewProps {
	badge?: ReactNode | FC;
	id: string;
	isDirty: boolean;
	name: string;
	onChange: (value: string) => void;
	options: RadioGroupOption[];
	title: ReactNode;
	value: string;
}

const RadioGroupFilterView: FC<RadioGroupFilterViewProps> = ({
	badge,
	id,
	isDirty,
	name,
	onChange,
	options,
	title,
	value,
}) => {
	const {
		maxWidth: { breakpointMobile: isMobile },
	} = useBreakpoint();
	const { isExpandableFilter } = useFilterView();
	const Badge = typeof badge === 'function' ? badge : () => <>{badge ?? null}</>;
	const [selected, setSelected] = useState(value);

	useEffect(() => {
		setSelected(value);
	}, [value]);

	const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
		setSelected(target.value);
		onChange(target.value);
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const radioList = (_isOpen?: boolean) => (
		<div className={styles.radioList}>
			{options.map((option) => (
				<Radio
					key={option.value}
					defaultChecked={selected === option.value}
					formProps={{
						input: {
							name,
							onChange: handleChange,
							value: option.value,
						},
					}}
					id={`${option.value}-${name}`}
					label={option.label}
				/>
			))}
		</div>
	);

	return (
		<>
			{isExpandableFilter ? (
				<ExpandableFilter
					badge={Badge}
					classNames={{
						content: styles.expandableRadioGroup,
						expanded: styles.expandedRadioGroup,
					}}
					id={id}
					isDirty={isDirty}
					title={title as NonNullable<React.ReactNode>}
				>
					{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
					{radioList as any}
				</ExpandableFilter>
			) : (
				<div className={cx('mw-mt-3 mw-mb-3', { 'mw-mb-2': isMobile })}>
					<LabelContainer className="mw-mt-3 mw-mb-1" label={title} labelAfter={<Badge />} />
					{radioList()}
				</div>
			)}
		</>
	);
};

export default RadioGroupFilterView;
